import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import formatValue from 'scoreboard/components/format-value'
import config from 'scoreboard/components/config'
import constants from 'scoreboard/components/constants'
import currency from 'scoreboard/components/currency'
import enums from 'scoreboard/components/enums'
import equationHighlighter from 'scoreboard/components/equations/equation-highlighter'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import momentHelper from 'scoreboard/components/moment-helper'
import seriesNames from '../series-names'
import metricEquationParser from 'scoreboard/scorecards/metric-equation-parser'
import groupType from 'scoreboard/admin/principals/groups/group-type'
import replicatedMessage from 'scoreboard/components/replicated-message'
import { removeEmptyValues }  from 'scoreboard/components/overview-info/generic-overview-info'
import showNativeCalendar from 'scoreboard/scorecards/show-native-calendar'
import DataType from 'scoreboard/datasets/model/data-type'
const { CURRENCY } = DataType
import addHrefToPrincipals from 'scoreboard/components/add-href-to-principals'

/**
 * Builds a context object used for displaying overview views
 * (PDF Export, Overview Dialog)
 *
 * @param model The scorecard model
 */
function context(model) {

	var calendar = _getCalendar(model.get('series'), model.get('calendarId'))
	var percentWeightingIgnoringPerformance = formatValue({
		value: model.get('type') === 'root' ? 1 : model.get('percentWeightingIgnoringPerformance'),
		dataType: 'percent'
	})
	var type = model.get('type')
	var typeText = i18n.get(enums.scorecardItemType[type])
	var typeLabel = i18n.get('common.info.withArg', typeText)
	var sourceType = model.get('sourceType')
	var sourceLabel = type === 'linked' ? model.get('sourceLabel') : undefined
	var sourceTypeLabel = sourceLabel && i18n.get(enums.scorecardItemType[sourceType])

	var yesNo, aggTypeKey, dataType, dataTypeKey, aggTypeLabel, calcAggTypeLabel,
		higherBetter, currencyLabel, rollupAggTypeLabel, rollupAggTypeKey,
		customTextGoal

	var scoringType = config.scoringType(model.get('scoringTypeId'))
	if (scoringType) {
		yesNo = _.get(scoringType, 'standardScoringType') == constants.YES_NO

		var aggType = model.get('aggregationType')
		//Edit screen shows these default aggregationType values if none is in the model
		var yesNoAggregationType = yesNo ? 'percentage-yes' : 'sum'
		var aggType = _.isString(aggType) ? aggType : yesNoAggregationType

		aggTypeKey = enums.aggregationType[aggType]
		dataType = model.get('dataType')
		dataTypeKey = enums.dataType[dataType]

		aggTypeLabel = i18n.get(aggTypeKey)
		calcAggTypeLabel = model.get('useEquationForAggregations') ?
			i18n.get('scorecards.node.metric.aggregationType.useEquation') :
			aggTypeLabel

		aggTypeLabel = _useCalculatedAggregation(model.get('series')) ? calcAggTypeLabel : aggTypeLabel
		var rollupAggType = model.get('rollupAggregationType')
		rollupAggTypeKey = enums.aggregationType[rollupAggType]
		rollupAggTypeLabel = i18n.get(rollupAggTypeKey)

		if (_.get(scoringType, 'standardScoringType') === constants.GOAL_ONLY) {
			higherBetter = _getBooleanAttribute(model.get('higherBetter'))
		}
		// #48277 Display "Goal" value for Scored Text scoring type KPIs
		if (_.get(scoringType, 'kpiValueType') === constants.SCORED_TEXT) {
			let goalThreshold = _.get(scoringType, `thresholds[${scoringType.goalIndex}]`)
			customTextGoal = _.get(goalThreshold, 'label')
		}
	}

	var precisionValue = model.get('precision')
	var precisionLabel = precisionValue && i18n.get('scorecard.builder.decimalPrecision' + precisionValue)

	var currencyCode = model.get('currencyCode')
	var currencyInfo = currency.forCode(currencyCode)
	if (type === 'metric' && dataType === CURRENCY) {
		currencyLabel = currencyInfo && currencyInfo.description
	}

	// information for the Details panel
	var details = {
		// Common to all Types
		'common.type': typeText,
		'scorecards.node.weight': percentWeightingIgnoringPerformance,

		// Advanced Options
		'admin.scorecards.startsOn': momentHelper.formatDate(model.get('start')),
		'admin.scorecards.archivesOn': momentHelper.formatDate(model.get('archive')),

		'report.customReport.scoringType' : scoringType && i18n.get(scoringType.label),
		'common.calendar.calendar': calendar && calendar.label,
		'scorecards.node.metric.higherValuesBetter': higherBetter,

		'scorecards.node.metric.isYesGood' : !yesNo ? undefined :
			_getBooleanAttribute(model.get('yesGood')),

		'scorecards.node.metric.threshold.goal' : customTextGoal,

		'scorecards.node.metric.dataType.dataType' :
			scoringType && i18n.get(dataTypeKey),

		'scorecards.node.metric.aggregationType.aggregationType' :
			_showManualAggregation(model.get('series'), scoringType) ? undefined : aggTypeLabel,

		'scorecards.node.metric.aggregationType.aggregationType.manual' :
			_showManualAggregation(model.get('series'), scoringType) ? aggTypeLabel : undefined,

		'scorecards.node.metric.aggregationType.aggregationType.calculated' :
			_showCalculatedAggregation(model.get('series'), scoringType) ? calcAggTypeLabel : undefined,

		'scorecards.node.metric.aggregationType.aggregationType.rollup' :
			_isSeriesAny(model.get('series'), 'rollup')? rollupAggTypeLabel : undefined,

		'scorecards.node.metric.decimalPrecision' : precisionLabel,
		'common.currency': currencyLabel,
		'scorecards.node.metric.dataType.units': model.get('unit'),
		'common.replication.replicated': replicatedMessage(model)
	}

	return {
		section: 'scorecards',
		id: config.displayIds ? model.get('id') : null,
		metricish: type === 'metric' || sourceType === 'metric',
		typeText,
		typeLabel,
		currencyCode,
		sourceId: model.get('sourceId'),
		templateId: model.get('templateId'),
		sourceOrganizationId: model.get('sourceOrganizationId'),
		template: model.get('template'),
		sourceLabel: sourceLabel,
		sourceTypeLabel: sourceTypeLabel,
		label: model.get('label'),
		periodLabel: calendars.periodsById[globals.periodId].label,
		description: escapePreserveLineBreaks(model.get('description')),
		detailList: removeEmptyValues(details),
		series: scoringType && _buildSeriesData(model.get('series'), scoringType.id, dataType, currencyCode,
			_.result(model, 'replicated')),
		scoringType: scoringType,
		dataType: dataType,
		chart: model.get('chart') || {},
		scores: model.get('scores'),
		tags: model.get('tags'),
		notes: model.get('notes'),
		owners: addHrefToPrincipals(groupType
			.groupTypeLabelAssignTo(model.get('owners'))),
		updaters: addHrefToPrincipals(groupType
			.groupTypeLabelAssignTo(model.get('updaters'))),
		copies: model.get('copies'),
		sourceOrganizationLabel: model.get('sourceOrganizationLabel'),

		/**
		 * Get an initiative detail value by its key
		 * @param key
		 * @returns {string}
		 */
		value: function(key) {
			return details[key]
		}


	}
}

/**
 * Updates the hash value for a boolean attribute that is nullable.
 * Will set value to 'Yes' or 'No'
 */
function _getBooleanAttribute(value) {
	if (value === true) {
		return i18n.get('common.yes')
	}
	else if (value === false) {
		return i18n.get('common.no')
	}
	return undefined
}


function _showCalculatedAggregation(series, scoringType) {
	var show = true
	if (scoringType && _.get(scoringType, 'standardScoringType') === constants.YES_NO) {
		show = false
	}
	else if (!_isSeriesAny(series, 'calculated')) {
		show = false
	}
	else if (_isSeriesAll(series, 'calculated')) {
		show = false
	}
	return show
}

// used to determine if we use "Aggregation Type" or "Manual Aggregation Type"
// label for the manual aggregation type
function _showManualAggregation(series, scoringType) {
	// #18037 updated rules: - label for manual aggregation type will be “Aggregation Type”,
	// except when "Calculated Aggregation Type" is present, then use "Manual Aggregation Type".
	// The label will still be “Aggregation Type” when only one aggregation type is present
	// (when all series are calculated, or for a calculated yes/no scoring type)
	var show = false
	if (_isSeriesAny(series, 'calculated') && !_isSeriesAll(series, 'calculated')) {
		show = true
	}
	if (scoringType && _.get(scoringType, 'standardScoringType') === constants.YES_NO) {
		show = false
	}
	return show
}

// used to determine if the aggregation type displayed
// should be the calculated aggregation type
// (when all series are calculated)
function _useCalculatedAggregation(series) {
	return _isSeriesAll(series, 'calculated')
}

function _isSeriesAll(series, updateType) {
	return _.every(series, function(s) {
		return s.updateType === updateType
	})
}

function _isSeriesAny(series, updateType) {
	return _.some(series, function(s) {
		return s.updateType === updateType
	})
}

function _buildSeriesData(series, scoringType, dataType, currencyCode, replicated) {
	var sk = seriesNames(scoringType)
	var series = _.map(series, function (s, i) {
		var mvtKey = enums.missingValueTreatment[s.missingValueTreatment]
		let equation = s.equationLabel ||
			equationHighlighter.highlight(metricEquationParser, s.equation).equation
		return {
			label: i18n.get(sk[i]),
			seriesKey: sk[i],
			//#21571 hide dataset metric info if replicated, SIPR users won't be able to see anything
			//because datasets aren't replicate themselves
			dataset: s.updateType === 'dataset' && !replicated,
			updateType: i18n.get('scorecards.node.metric.updateType.' + s.updateType),
			value: formatValue({ dataType: dataType, value: s.value, currencyCode: currencyCode}),
			missingValueTreatment: i18n.get(mvtKey),
			equation: equation
		}
	})
	return series
}

function _getCalendar(seriesArr, calendarId) {
	const showCalendar = showNativeCalendar(seriesArr)
	return showCalendar ? calendars.calendarsById[calendarId] : null
}

export default context
context.__filename__ = 'scorecards/overview/scorecards-context.js'
